import { useState } from 'react';
import { GRADIENT_PRIMARY_CLASS } from '../../foundation/tokens.js';
import TopbarApplicationIcon from './TopbarApplicationIcon.jsx';
import TopbarChip from './TopbarChip.jsx';
import TopbarTenantsSection from './TopbarTenantsSection.jsx';
import {
  dropdownAccountRowClass,
  dropdownDividerClass,
  dropdownMenuRow40Class,
  dropdownPanelClass,
  menuRowClass,
  topbarChipIdentityMobileClass,
  topbarProfileAvatarChipSizeClass,
} from './topbarClasses.js';
import Dropdown from '../Dropdown.jsx';
import Avatar from '../Avatar.jsx';
import Skeleton from '../Skeleton.jsx';
import Tooltip from '../Tooltip.jsx';
import { LogOut, Moon, PaintRoller, Sun } from 'lucide-react';

function WithTooltip({ content, children }) {
  if (!content) return children;
  return (
    <Tooltip
      content={content}
      arrow={false}
      position="bottom"
      align="start"
      zIndex={12050}
      triggerClassName="inline-flex w-fit"
    >
      {children}
    </Tooltip>
  );
}

export const TopbarRightMenuPanel = ({ accountMenu = {}, labels = {}, tooltips = {} }) => {
  const themeValue = accountMenu.theme?.value || 'auto';
  const themeOnChange = accountMenu.theme?.onChange;
  const themeTooltips = tooltips.theme || {};

  return (
    <div className={dropdownPanelClass}>
      <div className={dropdownAccountRowClass}>
        <Avatar
          size="sm"
          initials={accountMenu.user?.initials || 'MA'}
          className="!h-6 !w-6 !min-h-[1.5rem] !min-w-[1.5rem] shrink-0 border border-[var(--border\/primary\/lighter,#80afff)] bg-[var(--surface\/primary\/default,#2173ff)] text-[10px] font-semibold text-[var(--text\/negative,#ffffff)]"
        />
        <div className="min-w-0 flex-1">
          <p className="truncate text-[12px] font-semibold leading-none text-content-secondary">
            {accountMenu.user?.name || labels.unknownUser || ''}
          </p>
          <p className="truncate text-[11px] leading-none text-content-muted">
            {accountMenu.user?.email || ''}
          </p>
        </div>
      </div>

      <div className={dropdownDividerClass} />

      <TopbarTenantsSection
        tenants={accountMenu.tenants || []}
        currentTenantId={accountMenu.currentTenantId}
        onTenantSelect={accountMenu.onTenantSelect}
        onTenantSearch={accountMenu.onTenantSearch}
        organizationLabel={labels.organization || labels.workspace || 'Organization'}
        searchPlaceholder={labels.tenantSearchPlaceholder || labels.search}
        initialExpanded={Boolean(accountMenu.initialTenantsExpanded)}
        isLoading={Boolean(accountMenu.isLoading)}
        isSearchLoading={Boolean(accountMenu.isRefetching)}
        searchValue={accountMenu.searchValue}
        onSearchChange={accountMenu.onSearchChange}
        hasMore={Boolean(accountMenu.hasMore)}
        onLoadMore={accountMenu.onLoadMore}
        isLoadingMore={Boolean(accountMenu.isLoadingMore)}
      />

      <div className={dropdownDividerClass} />

      {(accountMenu.items || []).map((item) => {
        const Icon = item.icon;
        return (
          <button
            key={item.id}
            type="button"
            onClick={() => item.onClick?.(item)}
            className={dropdownMenuRow40Class}
          >
            {Icon ? <Icon size={20} className="shrink-0 text-content-muted" aria-hidden /> : null}
            <span className="text-[12px] font-medium leading-none">{item.label}</span>
          </button>
        );
      })}

      <div className={[menuRowClass, 'gap-4'].join(' ')}>
        <PaintRoller size={20} className="shrink-0 text-content-muted" aria-hidden />
        <span className="text-[12px] font-medium leading-none">{labels.theme || 'Theme'}</span>
        <div className="ml-auto flex items-center gap-1">
<WithTooltip content={themeTooltips.light || null}>
            <button
              type="button"
              onClick={() => themeOnChange?.('light')}
              aria-pressed={themeValue === 'light'}
              title={!themeTooltips.light ? (labels.lightMode || 'Light mode') : undefined}
              aria-label={labels.lightMode || 'Light mode'}
              className={[
                'inline-flex h-7 w-7 items-center justify-center rounded-lg transition-colors',
                themeValue === 'light'
                  ? `${GRADIENT_PRIMARY_CLASS} text-white`
                  : 'text-content-muted hover:bg-surface-hover-subtle hover:text-content',
              ].join(' ')}
            >
              <Sun size={16} aria-hidden />
            </button>
          </WithTooltip>
          <WithTooltip content={themeTooltips.dark || null}>
            <button
              type="button"
              onClick={() => themeOnChange?.('dark')}
              aria-pressed={themeValue === 'dark'}
              title={!themeTooltips.dark ? (labels.darkMode || 'Dark mode') : undefined}
              aria-label={labels.darkMode || 'Dark mode'}
              className={[
                'inline-flex h-7 w-7 items-center justify-center rounded-lg transition-colors',
                themeValue === 'dark'
                  ? `${GRADIENT_PRIMARY_CLASS} text-white`
                  : 'text-content-muted hover:bg-surface-hover-subtle hover:text-content',
              ].join(' ')}
            >
              <Moon size={16} aria-hidden />
            </button>
          </WithTooltip>
        </div>
      </div>

      <div className={dropdownDividerClass} />

      <button type="button" onClick={accountMenu.onSignOut} className={dropdownMenuRow40Class}>
        <LogOut size={20} className="shrink-0 text-content-muted" aria-hidden />
        <span className="text-[12px] font-medium leading-none">{labels.signOut || 'Sign out'}</span>
      </button>
    </div>
  );
};

const TopbarRightMenu = ({
  company,
  accountMenu = {},
  labels = {},
  tooltips = {},
  studio,
  selectedApplication,
  className = '',
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const tenants = accountMenu.tenants ?? [];
  const isLoading = Boolean(accountMenu.isLoading);
  // No useMemo needed for find over a small array — direct evaluation.
  const selectedTenant = tenants.find((tenant) => tenant.id === accountMenu.currentTenantId);
  const companyLabel =
    selectedTenant?.name || company?.label || labels.tenant || labels.company || 'Account';
  const profileTooltip = tooltips.profile || tooltips.account || tooltips.companyProfile;
  const profileInitials =
    accountMenu.user?.initials ||
    company?.userBadge ||
    'M';
  const profileBadge = (
    <Avatar size="sm" initials={profileInitials} className={topbarProfileAvatarChipSizeClass} />
  );

  const applicationIcon = (
    <TopbarApplicationIcon logo={selectedApplication?.logo || studio?.logo} />
  );

  // Show the skeleton only when loading AND there is truly no tenant name to
  // display (i.e. the very first render before any API data arrives).
  // During search refetches isLoading is also true but selectedTenant is
  // already populated from the previous results, so the chip must stay stable.
  const chip = isLoading && !selectedTenant ? (
    <div
      role="status"
      aria-label={labels.loadingAccount || 'Loading account'}
      className={[
        'inline-flex w-fit items-center gap-2 rounded-full px-1 py-0.5',
        topbarChipIdentityMobileClass,
      ].join(' ')}
    >
      <Skeleton width={16} height={16} borderRadius="9999px" className="shrink-0" />
      <Skeleton width={64} height={10} borderRadius="3px" className="hidden md:block" />
      <Skeleton width={16} height={16} borderRadius="9999px" className="shrink-0" />
    </div>
  ) : (
    <TopbarChip
      label={companyLabel}
      hideLabelOnMobile
      hideChevronOnMobile
      logo={applicationIcon}
      labelMaxWidthClass="min-w-0 max-w-[10ch] md:max-w-[14ch]"
      withLabelTooltip
      tooltipContent={companyLabel}
      rightContent={
        profileTooltip && !menuOpen ? (
          <Tooltip
            content={profileTooltip}
            arrow={false}
            position="bottom"
            align="start"
            triggerClassName="inline-flex w-fit shrink-0"
          >
            {profileBadge}
          </Tooltip>
        ) : (
          profileBadge
        )
      }
      className={['w-fit', topbarChipIdentityMobileClass].join(' ')}
    />
  );

  return (
    <Dropdown
      align="right"
      usePortal
      menuMaxHeight="none"
      menuClassName="w-80 border-none bg-transparent p-0 shadow-none"
      onOpenChange={setMenuOpen}
      trigger={
        <div
          className={[className, 'inline-flex w-fit overflow-visible'].filter(Boolean).join(' ')}
          role="group"
          aria-label={companyLabel || labels.tenant || 'Account menu'}
        >
          {chip}
        </div>
      }
      className="inline-flex max-w-full overflow-visible"
    >
      <TopbarRightMenuPanel accountMenu={accountMenu} labels={labels} tooltips={tooltips} />
    </Dropdown>
  );
};

export default TopbarRightMenu;
